import { api } from "@/lib/api";
import { ActionIcon, Alert, Button, Group, Modal, Pagination, Paper, Select, Stack, Table, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconArrowsSort, IconEdit, IconSend, IconSortAscendingLetters, IconSortDescendingLetters, IconStarOff, IconUserPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import CountySelect from "../CountySelect";

export type CountyAdmin = {
    id: number;
    firstName: string;
    lastName: string;
    department: string;
    email: string;
    role: string;
    county: string;
    countyCode: string;
    countyId: number;
    isActive: boolean;
    userId: number;
}

const CountyAdmins = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [sortTerm, setSortTerm] = useState('county asc');
    const [countyAdmins, setCountyAdmins] = useState<CountyAdmin[]>([]);
    const [visibleCountyAdmins, setVisibleCountyAdmins] = useState<CountyAdmin[]>([]);
    const [filteredCountyAdmins, setFilteredCountyAdmins] = useState<CountyAdmin[]>([]);
    const [selectedCountyAdmin, setSelectedCountyAdmin] = useState<CountyAdmin | null>(null);
    const [demoteModalOpened, demoteModalCallbacks] = useDisclosure(false);
    const [addModalOpened, addModalCallbacks] = useDisclosure(false);
    const [addModalError, setAddModalError] = useState<string | null>(null);
    const [addModalSuccess, setAddModalSuccess] = useState<boolean>(false);
    const [editModalOpened, editModalCallbacks] = useDisclosure(false);
    const [editModalError, setEditModalError] = useState<string | null>(null);
    const [editModalSuccess, setEditModalSuccess] = useState<boolean>(false);
    const [resendInviteModalOpened, resendInviteModalCallbacks] = useDisclosure(false);
    const [filterCounty, setFilterCounty] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<{
        email: string;
        firstName: string;
        lastName: string;
        department: string;
        countyCode: string;
    }>({ email: '', firstName: '', lastName: '', department: '', countyCode: '' });
    const [selectedCounty, setSelectedCounty] = useState<{
        name: string;
        mask: string;
        format: string;
        code: string;
    } | null>(null);
    useEffect(() => {
        fetchCountyAdmins();
    }, []);
    const fetchCountyAdmins = async () => {
        try {
            const response: CountyAdmin[] = await api.get(`/admin/county-admins`);
            setCountyAdmins(response);
        } catch (error) {
            console.error('Error fetching county admins:', error);
        }
    }
    useEffect(() => {
        let newCountyAdmins = [...countyAdmins]
        if (searchTerm) {
            newCountyAdmins = [...newCountyAdmins.filter((countyAdmin) => {
                return countyAdmin?.firstName?.toLowerCase().includes(searchTerm?.toLowerCase()) || countyAdmin?.lastName?.toLowerCase().includes(searchTerm?.toLowerCase()) || countyAdmin?.county?.toLowerCase().includes(searchTerm?.toLowerCase()) || countyAdmin?.email?.toLowerCase().includes(searchTerm?.toLowerCase()) || countyAdmin?.department?.toLowerCase().includes(searchTerm?.toLowerCase());
            })];
        }
        if (filterCounty) {
            newCountyAdmins = newCountyAdmins.filter((countyAdmin) => countyAdmin.county === filterCounty);
        }
        else{
            newCountyAdmins = [...newCountyAdmins];
        }
        setFilteredCountyAdmins(newCountyAdmins);
        setVisibleCountyAdmins([...newCountyAdmins.sort((a, b) => {
            if (sortTerm.includes('firstName')) {
                return sortTerm.includes('desc') ? b.firstName.localeCompare(a.firstName) : a.firstName.localeCompare(b.firstName);
            } else if (sortTerm.includes('lastName')) {
                return sortTerm.includes('desc') ? b.lastName.localeCompare(a.lastName) : a.lastName.localeCompare(b.lastName);
            }
            else if (sortTerm.includes('county')) {
                return sortTerm.includes('desc') ? b.county.localeCompare(a.county) : a.county.localeCompare(b.county);
            }
            else if (sortTerm.includes('email')) {
                return sortTerm.includes('desc') ? b.email.localeCompare(a.email) : a.email.localeCompare(b.email);
            }
            else if (sortTerm.includes('department')) {
                return sortTerm.includes('desc') ? b.department.localeCompare(a.department) : a.department.localeCompare(b.department);
            }
            return 0;
        }).slice(page * pageSize, (page + 1) * pageSize)]);
    }, [page, pageSize, sortTerm, searchTerm, countyAdmins, filterCounty]);

    const handleAttemptDemoteCountyAdmin = (countyAdmin: CountyAdmin) => {
        setSelectedCountyAdmin(countyAdmin);
        demoteModalCallbacks.open();
    }
    const handleDemoteCountyAdmin = async () => {
        if (!selectedCountyAdmin) {
            return;
        }
        try {
            await api.post(`/admin/demote-user`, { userId: selectedCountyAdmin.userId, countyCode: selectedCountyAdmin.countyCode });
            fetchCountyAdmins();
            demoteModalCallbacks.close();
        } catch (error) {
            console.error('Error demoting county admin:', error);
        }
    }
    const handleAttemptAddCountyAdmin = () => {
        addModalCallbacks.open();
    }
    const handleAddCountyAdmin = async () => {
        try {
            await api.put(`/admin/county-admin/add`, {
                email: selectedUser?.email,
                firstName: selectedUser?.firstName,
                lastName: selectedUser?.lastName,
                department: selectedUser?.department,
                countyCode: selectedCounty?.code
            });
            fetchCountyAdmins();
            setAddModalSuccess(true);
            setTimeout(() => {
                setAddModalSuccess(false);
            }, 3000);
            addModalCallbacks.close();
        } catch (error) {
            console.error('Error adding county admin:', error);
            setAddModalError('Failed to add county admin. Please check the details and try again.');
        }
    }

    const handleEditCountyAdmin = async () => {
        try {
            await api.post(`/admin/county-admins/`, {
                countyAdministratorId: selectedCountyAdmin?.id,
                email: selectedCountyAdmin?.email,
                firstName: selectedCountyAdmin?.firstName,
                lastName: selectedCountyAdmin?.lastName,
                department: selectedCountyAdmin?.department,
                countyName: selectedCountyAdmin?.county
            });
            fetchCountyAdmins();
            setEditModalSuccess(true);
            setTimeout(() => {
                setEditModalSuccess(false);
            }, 3000);
            editModalCallbacks.close();
        } catch (error) {
            console.error('Error editing county admin:', error);
            setEditModalError('Failed to edit county admin. Please check the details and try again.');
        }
    }

    const handleAttemptResendWelcomeEmail = (countyAdmin: CountyAdmin) => {
        setSelectedCountyAdmin(countyAdmin);
        resendInviteModalCallbacks.open();
    }

    const handleResendInvitation = async () => {
        try {
            await api.post(`/admin/county-admin/${selectedCountyAdmin!.id}/invite/resend`, {});
            fetchCountyAdmins();
            resendInviteModalCallbacks.close();
        } catch (error) {
            console.error('Error resending invitation:', error);
        }
    }

    return (
        <>
            <Paper withBorder p='md' radius={'md'}>
                <Group justify='space-between'>
                    <Title order={4}>County Admins</Title>
                    <Tooltip label={'Add County Admin'} withArrow>
                        <ActionIcon aria-label="Add County Admin" variant='outline' color='pq-red' onClick={() => { handleAttemptAddCountyAdmin(); }}>
                            <IconUserPlus size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
                <Stack>
                    {addModalSuccess && <Alert color='green' mt='md'>
                        <Text>Successfully Added County Admin</Text>
                    </Alert>}
                    {editModalSuccess && <Alert color='green' mt='md'>
                        <Text>Successfully Edited County Admin</Text>
                    </Alert>}
                    <Group justify='flex-end' mt='md'>
                        <TextInput
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.currentTarget.value)}
                        />
                        <CountySelect onChange={(county) => {
                            filterCounty === county.name ? setFilterCounty("") : setFilterCounty(county.name);
                        }} />
                        <Select
                            value={pageSize.toString()}
                            onChange={(value) => setPageSize(Number(value))}
                            data={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                                { value: '100', label: '100' }
                            ]}
                        />
                        <Pagination
                            total={Math.ceil(filteredCountyAdmins.length / pageSize)}
                            value={page + 1}
                            onChange={(value) => {
                                setPage(value - 1);
                            }}
                        />
                    </Group>
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                    const newSortTerm = sortTerm.includes('firstName') ? (sortTerm.includes('desc') ? 'firstName asc' : 'firstName desc') : 'firstName asc';
                                    setSortTerm(newSortTerm);
                                }}><Text>First Name</Text>{sortTerm.includes('firstName') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                    const newSortTerm = sortTerm.includes('lastName') ? (sortTerm.includes('desc') ? 'lastName asc' : 'lastName desc') : 'lastName asc';
                                    setSortTerm(newSortTerm);
                                }}><Text>Last Name</Text>{sortTerm.includes('lastName') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                    const newSortTerm = sortTerm.includes('email') ? (sortTerm.includes('desc') ? 'email asc' : 'email desc') : 'email asc';
                                    setSortTerm(newSortTerm);
                                }}><Text>Email</Text>{sortTerm.includes('email') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                    const newSortTerm = sortTerm.includes('county') ? (sortTerm.includes('desc') ? 'county asc' : 'county desc') : 'county asc';
                                    setSortTerm(newSortTerm);
                                }}><Text>County</Text>{sortTerm.includes('county') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                    const newSortTerm = sortTerm.includes('department') ? (sortTerm.includes('desc') ? 'department asc' : 'department desc') : 'department asc';
                                    setSortTerm(newSortTerm);
                                }}><Text>Department</Text>{sortTerm.includes('department') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                <Table.Th><Group><Text>Actions</Text></Group></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {visibleCountyAdmins.map((countyAdmin) => (
                                <Table.Tr key={countyAdmin.id}>
                                    <Table.Td>{countyAdmin.firstName}</Table.Td>
                                    <Table.Td>{countyAdmin.lastName}</Table.Td>
                                    <Table.Td>{countyAdmin.email}</Table.Td>
                                    <Table.Td>{countyAdmin.county}</Table.Td>
                                    <Table.Td>{countyAdmin.department}</Table.Td>
                                    <Table.Td>
                                        <Group>
                                            <Tooltip label={`Remove ${countyAdmin.firstName} ${countyAdmin.lastName}`} withArrow>
                                                <ActionIcon aria-label={`Remove ${countyAdmin.firstName} ${countyAdmin.lastName}`} variant='outline' color='pq-red' onClick={() => { handleAttemptDemoteCountyAdmin(countyAdmin); }}>
                                                    <IconStarOff size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                            <Tooltip label={`Edit ${countyAdmin.firstName} ${countyAdmin.lastName}`} withArrow>
                                                <ActionIcon aria-label={`Edit ${countyAdmin.firstName} ${countyAdmin.lastName}`} variant='outline' color='blue' onClick={() => {
                                                    setSelectedCountyAdmin(countyAdmin);
                                                    editModalCallbacks.open();
                                                }}>
                                                    <IconEdit size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                            {countyAdmin.isActive ? <Text c='green'>Active</Text> : <Tooltip label={`Resend Welcome Email to ${countyAdmin.firstName} ${countyAdmin.lastName}`} withArrow>
                                                <ActionIcon aria-label={`Resend Welcome Email to ${countyAdmin.firstName} ${countyAdmin.lastName}`} variant='outline' color='pq-red' onClick={() => { handleAttemptResendWelcomeEmail(countyAdmin); }}>
                                                    <IconSend size={16} />
                                                </ActionIcon>
                                            </Tooltip>}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                    <Group justify='flex-end' mt='md'>
                        <Select
                            value={pageSize.toString()}
                            onChange={(value) => setPageSize(Number(value))}
                            data={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                                { value: '100', label: '100' }
                            ]}
                        />
                        <Pagination
                            total={Math.ceil(filteredCountyAdmins.length / pageSize)}
                            value={page + 1}
                            onChange={(value) => {
                                setPage(value - 1);
                            }}
                        />
                    </Group>
                </Stack>
            </Paper>
            <Modal
                opened={demoteModalOpened}
                onClose={() => demoteModalCallbacks.close()}
                title="Demote County Admin"
            >
                <Text>Are you sure you want to demote {selectedCountyAdmin?.firstName} {selectedCountyAdmin?.lastName}?</Text>
                <Group mt='md' justify='flex-end'>
                    <Button variant='default' onClick={() => demoteModalCallbacks.close()}>Cancel</Button>
                    <Button color='pq-red' onClick={() => handleDemoteCountyAdmin()}>Demote</Button>
                </Group>
            </Modal>
            <Modal opened={editModalOpened} onClose={editModalCallbacks.close} title="Edit County Admin">
                <Stack>
                    {editModalError && <Alert color='red'>
                        <Text>{editModalError}</Text>
                    </Alert>}
                    <TextInput label="County" value={selectedCountyAdmin?.county} readOnly />
                    <TextInput
                        label="Department"
                        placeholder="Enter department"
                        value={selectedCountyAdmin?.department}
                        onChange={(e) => {
                            if (selectedCountyAdmin) {
                                setSelectedCountyAdmin({ ...selectedCountyAdmin, department: e.currentTarget.value });
                            }
                        }}
                    />
                    <TextInput
                        label="First Name"
                        placeholder="Enter first name"
                        value={selectedCountyAdmin?.firstName}
                        onChange={(e) => {
                            if (selectedCountyAdmin) {
                                setSelectedCountyAdmin({ ...selectedCountyAdmin, firstName: e.currentTarget.value });
                            }
                        }}
                    />
                    <TextInput
                        label="Last Name"
                        placeholder="Enter last name"
                        value={selectedCountyAdmin?.lastName}
                        onChange={(e) => {
                            if (selectedCountyAdmin) {
                                setSelectedCountyAdmin({ ...selectedCountyAdmin, lastName: e.currentTarget.value });
                            }                     }}
                    />
                    <TextInput
                        label="Email"
                        placeholder="Enter email"
                        value={selectedCountyAdmin?.email} 
                        onChange={(e) => {
                            if (selectedCountyAdmin) {
                                setSelectedCountyAdmin({ ...selectedCountyAdmin, email: e.currentTarget.value });
                            }
                        }}
                    />
                    <Group justify='flex-end'>
                        <Button variant="outline" onClick={editModalCallbacks.close}>Cancel</Button>
                        <Button onClick={() => handleEditCountyAdmin()}>Save</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal opened={addModalOpened} onClose={addModalCallbacks.close} title="Add County Admin">
                <Stack>
                    {addModalError && <Alert color='red'>
                        <Text>{addModalError}</Text>
                    </Alert>}
                    <CountySelect label="Select County to Administer" onChange={(county) => {
                        setSelectedCounty(county);
                    }} />
                    <TextInput
                        label="Department"
                        placeholder="Enter department"
                        value={selectedUser?.department}
                        onChange={(e) => {
                            if (selectedUser) {
                                setSelectedUser({ ...selectedUser, department: e.currentTarget.value });
                            }
                        }}
                    />
                    <TextInput
                        label="First Name"
                        placeholder="Enter first name"
                        value={selectedUser?.firstName}
                        onChange={(e) => {
                            if (selectedUser) {
                                setSelectedUser({ ...selectedUser, firstName: e.currentTarget.value });
                            }
                        }}
                    />
                    <TextInput
                        label="Last Name"
                        placeholder="Enter last name"
                        value={selectedUser?.lastName}
                        onChange={(e) => {
                            if (selectedUser) {
                                setSelectedUser({ ...selectedUser, lastName: e.currentTarget.value });
                            }
                        }}
                    />
                    <TextInput
                        label="Email"
                        placeholder="Enter email"
                        value={selectedUser?.email}
                        onChange={(e) => {
                            if (selectedUser) {
                                setSelectedUser({ ...selectedUser, email: e.currentTarget.value });
                            }
                        }}
                    />
                    <Group justify='flex-end'>
                        <Button variant="outline" onClick={addModalCallbacks.close}>Cancel</Button>
                        <Button onClick={() => handleAddCountyAdmin()}>Add</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal
                opened={resendInviteModalOpened}
                onClose={() => resendInviteModalCallbacks.close()}
                title="Resend Invitation"
            >
                <Text>Are you sure you want to resend the invitation to {selectedUser?.firstName} {selectedUser?.lastName}?</Text>
                <Group mt='md' justify='flex-end'>
                    <Button variant='default' onClick={() => resendInviteModalCallbacks.close()}>Cancel</Button>
                    <Button color='pq-red' onClick={() => {
                        handleResendInvitation();
                    }}>Resend</Button>
                </Group>
            </Modal>
        </>
    );
}

export default CountyAdmins;